<template>
  <v-container class="answer-container">
    <v-row>
      <v-col cols="12">
        <v-card class="pa-3 answer-card" outlined>
          <v-card-text class="answer-text">
            <vue-markdown
              :anchorAttributes="{ target: '_blank' }"
              :source="parsedAnswer.markdownFormatText"
              class="answer-text"
            />
          </v-card-text>
          <v-card-actions>
            <v-row justify="space-between">
              <v-col
                class="d-flex align-center text-caption grey--text text--darken-2"
                lg
              >
                <v-icon class="mr-1" small color="grey darken-1"
                  >mdi-information-outline</v-icon
                >
                <div>{{ $t('aiChat.aiGeneratedNote') }}</div>
              </v-col>
              <v-col md="auto" class="d-flex align-center justify-end">
                <v-btn small icon @click="onLikeResponseClicked">
                  <v-icon
                    small
                    :color="
                      currentFeedbackState === Feedback.Positive
                        ? 'green darken-4'
                        : 'grey'
                    "
                    >mdi-thumb-up</v-icon
                  >
                </v-btn>
                <v-btn small icon @click="onDislikeResponseClicked">
                  <v-icon
                    small
                    :color="
                      currentFeedbackState !== Feedback.Positive &&
                      currentFeedbackState !== Feedback.Neutral &&
                      currentFeedbackState !== ''
                        ? 'red darken-4'
                        : 'grey'
                    "
                    >mdi-thumb-down</v-icon
                  >
                </v-btn>
              </v-col>
            </v-row>
          </v-card-actions>
          <v-card-text>
            <v-row v-if="parsedAnswer.citations.length > 0">
              <v-col cols="12">
                <v-expansion-panels light>
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <span
                        >{{ parsedAnswer.citations.length }}
                        {{
                          $tc(
                            'aiChat.referencesPlural',
                            parsedAnswer.citations.length
                          )
                        }}</span
                      >
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-list>
                        <v-list-item
                          v-for="(citation, idx) in parsedAnswer.citations"
                          :key="idx"
                          @click="onViewSource(citation)"
                        >
                          <v-list-item-content>
                            <v-list-item-title
                              :title="createCitationFilepath(citation, idx)"
                              :aria-label="
                                createCitationFilepath(citation, idx)
                              "
                            >
                              <div class="citation mr-1">{{ idx + 1 }}</div>
                              {{ createCitationFilepath(citation, idx, false) }}
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog
      v-model="isFeedbackDialogOpen"
      v-if="isFeedbackDialogOpen"
      max-width="600px"
    >
      <v-card>
        <v-card-title class="headline d-flex justify-space-between">
          {{ $t('aiChat.submitFeedback') }}
          <v-btn
            rounded
            @click="resetFeedbackDialog"
            class="close-btn"
            height="40"
            width="40"
            icon
          >
            <v-icon small>$close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <div>{{ $t('aiChat.yourFeedbackTitle') }}</div>
                <div v-if="!showReportInappropriateFeedback">
                  <div>{{ $t('aiChat.feedbackPlaceholder') }}</div>
                  <v-row>
                    <v-col cols="12">
                      <v-checkbox
                        hide-details
                        :label="missingCitationLabel"
                        :value="Feedback.MissingCitation"
                        v-model="negativeFeedbackList"
                      ></v-checkbox>
                      <v-checkbox
                        hide-details
                        :label="wrongCitationLabel"
                        :value="Feedback.WrongCitation"
                        v-model="negativeFeedbackList"
                      ></v-checkbox>
                      <v-checkbox
                        hide-details
                        :label="outOfScopeLabel"
                        :value="Feedback.OutOfScope"
                        v-model="negativeFeedbackList"
                      ></v-checkbox>
                      <v-checkbox
                        hide-details
                        :label="inaccurateOrIrrelevantLabel"
                        :value="Feedback.InaccurateOrIrrelevant"
                        v-model="negativeFeedbackList"
                      ></v-checkbox>
                      <v-checkbox
                        hide-details
                        :label="otherFeedbackLabel"
                        :value="Feedback.OtherUnhelpful"
                        v-model="negativeFeedbackList"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" class="pt-0 mb-3">
                      <div
                        @click="showReportInappropriateFeedback = true"
                        style="color: #115ea3; cursor: pointer"
                      >
                        {{ reportInappropriateContentLabel }}
                      </div>
                    </v-col>
                  </v-row>
                </div>
                <div v-else>
                  <div>
                    {{ theContentIsLabel }} <span style="color: red">*</span>
                  </div>
                  <v-row>
                    <v-col cols="12">
                      <v-col cols="12">
                        <v-checkbox
                          hide-details
                          :label="hateSpeechLabel"
                          :value="Feedback.HateSpeech"
                          v-model="negativeFeedbackList"
                        ></v-checkbox>
                        <v-checkbox
                          hide-details
                          :label="violentLabel"
                          :value="Feedback.Violent"
                          v-model="negativeFeedbackList"
                        ></v-checkbox>
                        <v-checkbox
                          hide-details
                          :label="sexualLabel"
                          :value="Feedback.Sexual"
                          v-model="negativeFeedbackList"
                        ></v-checkbox>
                        <v-checkbox
                          hide-details
                          :label="manipulativeLabel"
                          :value="Feedback.Manipulative"
                          v-model="negativeFeedbackList"
                        ></v-checkbox>
                        <v-checkbox
                          hide-details
                          :label="otherFeedbackLabel"
                          :value="Feedback.OtherHarmful"
                          v-model="negativeFeedbackList"
                        ></v-checkbox>
                      </v-col>
                    </v-col>
                  </v-row>
                </div>
                <div>
                  {{ $t('aiChat.submitLabel') }}
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            class="mb-2"
            :disabled="negativeFeedbackList.length < 1"
            @click="onSubmitNegativeFeedback"
            >{{ $t('global.submit') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import VueMarkdown from 'vue-markdown'
import {
  Feedback,
  AskResponse,
  ParsedAnswer,
  Citation,
} from '@/constants/aiChatModels'
import { cloneDeep } from 'lodash'
import { mapActions, mapState } from 'vuex'

export default defineComponent({
  name: 'Answer',
  components: {
    VueMarkdown,
  },
  props: {
    answer: {
      type: Object as PropType<AskResponse>,
      default: () => ({}),
    },
    vSessionId: {
      type: String,
      default: '',
    },
  },
  watch: {
    answer: {
      handler() {
        this.initAnswerFeedbackState()
      },
      immediate: true,
    },
  },
  data() {
    return {
      Feedback,
      SANITIZE_ANSWER: true,
      answerFeedbackState: Feedback.Neutral,
      isFeedbackDialogOpen: false,
      showReportInappropriateFeedback: false,
      negativeFeedbackList: [],
    }
  },
  computed: {
    ...mapState('Chat', ['feedbackState']),
    missingCitationLabel() {
      return this.$t('aiChat.missingCitation')
    },
    wrongCitationLabel() {
      return this.$t('aiChat.wrongCitation')
    },
    outOfScopeLabel() {
      return this.$t('aiChat.outOfScope')
    },
    inaccurateOrIrrelevantLabel() {
      return this.$t('aiChat.inaccurateOrIrrelevant')
    },
    reportInappropriateContentLabel() {
      return this.$t('aiChat.reportInappropriateContent')
    },
    theContentIsLabel() {
      return this.$t('aiChat.theContentIs')
    },
    hateSpeechLabel() {
      return this.$t('aiChat.hateSpeech')
    },
    violentLabel() {
      return this.$t('aiChat.violent')
    },
    sexualLabel() {
      return this.$t('aiChat.sexual')
    },
    manipulativeLabel() {
      return this.$t('aiChat.manipulative')
    },
    submitLabel() {
      return this.$t('aiChat.submitLabel')
    },
    otherFeedbackLabel() {
      return this.$t('aiChat.otherFeedback')
    },
    parsedAnswer() {
      return this.parseAnswer(this.answer)
    },
    currentFeedbackState() {
      const feedbackState =
        this.feedbackState[this.answer.message_id] ?? this.answerFeedbackState
      return feedbackState
    },
  },
  methods: {
    ...mapActions('Chat', ['historyMessageFeedback', 'setFeedbackState']),
    async onLikeResponseClicked() {
      if (this.answer.message_id == undefined) return

      let newFeedbackState = this.answerFeedbackState
      // Set or unset the thumbs up state
      if (this.answerFeedbackState == Feedback.Positive) {
        newFeedbackState = Feedback.Neutral
      } else {
        newFeedbackState = Feedback.Positive
      }
      this.setFeedbackState({
        answerId: this.answer.message_id,
        feedback: newFeedbackState,
      })

      // Update message feedback in db
      const response = await this.historyMessageFeedback({
        message_id: this.answer.message_id,
        message_feedback: newFeedbackState,
        config: {
          vSessionId: this.vSessionId,
        },
      })

      if (response?.ok) {
        this.answerFeedbackState = newFeedbackState
      } else {
        this.setFeedbackState({
          answerId: this.answer.message_id,
          feedback: this.answerFeedbackState,
        })
      }
    },
    initAnswerFeedbackState() {
      this.answerFeedbackState = this.answer.feedback ?? Feedback.Neutral
    },
    async onDislikeResponseClicked() {
      if (this.answer.message_id == undefined) return

      let newFeedbackState = this.answerFeedbackState
      if (
        this.answerFeedbackState === undefined ||
        this.answerFeedbackState === Feedback.Neutral ||
        this.answerFeedbackState === Feedback.Positive
      ) {
        this.isFeedbackDialogOpen = true
      } else {
        // Reset negative feedback to neutral
        newFeedbackState = Feedback.Neutral
        this.setFeedbackState({
          answerId: this.answer.message_id,
          feedback: newFeedbackState,
        })
        const response = await this.historyMessageFeedback({
          message_id: this.answer.message_id,
          message_feedback: newFeedbackState,
          config: {
            vSessionId: this.vSessionId,
          },
        })
        if (response?.ok) {
          this.setFeedbackState({
            answerId: this.answer.message_id,
            feedback: newFeedbackState,
          })
          this.answerFeedbackState = newFeedbackState
        } else {
          this.setFeedbackState({
            answerId: this.answer.message_id,
            feedback: this.answerFeedbackState,
          })
        }
      }
    },
    createCitationFilepath(
      citation: Citation,
      index: number,
      truncate = false
    ) {
      let citationFilename = ''
      if (citation.filepath) {
        if (truncate && citation.filepath.length > 50) {
          citationFilename = `${citation.filepath.substring(0, 20)}`
        } else {
          citationFilename = `${citation.filepath}`
        }
      } else {
        citationFilename = `Citation ${index}`
      }
      return citationFilename
    },
    onViewSource(citation) {
      if (citation.url) {
        const pageIndex = citation.content.indexOf('_pages_')
        if (pageIndex > 0) {
          const page = parseInt(
            citation.content.substring(pageIndex + 7).replace(/\\/g, ''),
            10
          )
          const url = citation.url + '#page=' + (page + 1)
          window.open(url, '_blank')
        } else {
          window.open(citation.url, '_blank')
        }
      }
    },
    resetFeedbackDialog() {
      this.isFeedbackDialogOpen = false
      this.showReportInappropriateFeedback = false
      this.negativeFeedbackList = []
    },
    async onSubmitNegativeFeedback() {
      if (this.answer.message_id == undefined) return
      const response = await this.historyMessageFeedback({
        message_id: this.answer.message_id,
        message_feedback: this.negativeFeedbackList.join(','),
        config: {
          vSessionId: this.vSessionId,
        },
      })
      if (response?.ok) {
        this.setFeedbackState({
          answerId: this.answer.message_id,
          feedback: Feedback.Negative,
        })
        this.answerFeedbackState = Feedback.Negative
      } else {
        this.setFeedbackState({
          answerId: this.answer.message_id,
          feedback: this.answerFeedbackState,
        })
      }
      this.resetFeedbackDialog()
    },

    enumerateCitations(citations: Citation[]) {
      const filepathMap = new Map()
      for (const citation of citations) {
        const { filepath } = citation
        let part_i = 1
        if (filepathMap.has(filepath)) {
          part_i = filepathMap.get(filepath) + 1
        }
        filepathMap.set(filepath, part_i)
        citation.part_index = part_i
      }
      return citations
    },

    parseAnswer(answer: AskResponse): ParsedAnswer {
      let answerText = answer.answer
      answerText = answerText.replace(
        /\[doc\d+(, p\.\d+)?(; doc\d+(, p\.\d+)?)*]/g,
        (match) => {
          const docMatches = match.match(/doc\d+/g)
          if (docMatches) {
            return docMatches.map((doc) => `[${doc}]`).join('')
          }
          return match
        }
      )
      const citationLinks = answerText.match(/\[(doc\d\d?\d?)]/g)

      const lengthDocN = '[doc'.length

      let filteredCitations = [] as Citation[]
      let citationReindex = 0
      citationLinks?.forEach((link) => {
        // Replacing the links/citations with number
        const citationIndex = link.slice(lengthDocN, link.length - 1)
        const citation = cloneDeep(
          answer.citations[Number(citationIndex) - 1]
        ) as Citation
        if (
          !filteredCitations.find((c) => c.id === citationIndex) &&
          citation
        ) {
          const escapedLink = link.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
          answerText = answerText.replace(
            new RegExp(escapedLink, 'g'),
            ` ^${++citationReindex}^ `
          )
          citation.id = citationIndex // original doc index to de-dupe
          citation.reindex_id = citationReindex.toString() // reindex from 1 for display
          filteredCitations.push(citation)
        }
      })

      filteredCitations = this.enumerateCitations(filteredCitations)

      return {
        citations: filteredCitations,
        markdownFormatText: answerText,
      }
    },
  },
})
</script>

<style lang="scss">
.answer-container {
  .answer-card {
    background: #fff;
    box-shadow: 0 1px 2px #00000024, 0 0 2px #0000001f;
    border-radius: 5.419px;
    .answer-text {
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      color: #323130;
    }
  }
  a {
    color: #25f !important;
  }
  p {
    margin: 0 !important;
  }
  .v-expansion-panel-header {
    min-height: 28px !important;
    font-size: 0.75rem;
    padding: 8px 16px !important;
  }
  .v-expansion-panel-content__wrap {
    padding: 0 18px 16px 4px;
  }
  .v-list-item {
    min-height: 28px !important;
    font-size: 0.75rem;
    padding: 0 4px;
  }
  .v-list-item__content {
    padding: 0 4px;
  }
  .v-list-item__title {
    font-size: 0.75rem;
    padding: 0 4px;
  }
  .citation {
    box-sizing: border-box;
    display: inline-flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 0px;
    width: 14px;
    height: 14px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    flex: none;
    flex-grow: 0;
    z-index: 2;
    font-style: normal;
    font-weight: 600;
    font-size: 10px;
    line-height: 14px;
    text-align: center;
    color: #424242;
    cursor: pointer;
  }
}
</style>
